import { useLocation, useNavigate, Link } from 'react-router-dom'
import {
  IconSun,
  IconMoon,
  IconCoin,
} from '@tabler/icons-react'
import { useTheme } from 'next-themes'
import { useTranslation } from 'react-i18next'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { cn } from '@/lib/utils'
import { usePermissions } from '@/lib/permissions'

type BreadcrumbEntry = { parentKey: string; parentUrl: string; childKey: string }

const breadcrumbMap: Record<string, BreadcrumbEntry> = {
  '/studio/team':       { parentKey: 'nav.studio',    parentUrl: '/studio/profile', childKey: 'nav.team' },
  '/studio/assessment': { parentKey: 'nav.studio',    parentUrl: '/studio/profile', childKey: 'nav.assessment' },
  '/studio/templates':  { parentKey: 'nav.studio',    parentUrl: '/studio/profile', childKey: 'nav.templates' },
  '/settings/audit':    { parentKey: 'nav.settings',  parentUrl: '/settings',       childKey: 'nav.auditLog' },
  '/billing/topup':     { parentKey: 'nav.billing',   parentUrl: '/billing',        childKey: 'nav.topup' },
}

const pageTitleKeys: Record<string, string> = {
  '/dashboard': 'nav.dashboard',
  '/clients':   'nav.clients',
  '/cases':     'nav.cases',
  '/deadlines': 'nav.deadlines',
  '/studio':    'nav.studio',
  '/billing':   'nav.billing',
  '/settings':  'nav.settings',
}

export function SiteHeader({ walletTotal }: { walletTotal?: number }) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const navigate = useNavigate()
  const breadcrumb = breadcrumbMap[location.pathname]
  const { theme, setTheme } = useTheme()
  const { can } = usePermissions()
  const isDark = theme === 'dark'
  const canOpenBilling = can('wallet', 'update')

  const getPageTitle = (pathname: string): string => {
    if (pageTitleKeys[pathname]) return t(pageTitleKeys[pathname]!)
    const prefix = Object.keys(pageTitleKeys).find(p => pathname.startsWith(p))
    return prefix ? t(pageTitleKeys[prefix]!) : 'Aegis'
  }

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mx-2 data-[orientation=vertical]:h-4"
        />

        {/* Breadcrumb or page title */}
        {breadcrumb ? (
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to={breadcrumb.parentUrl}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t(breadcrumb.parentKey)}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-sm font-medium">
                  {t(breadcrumb.childKey)}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        ) : (
          <h1 className="text-base font-medium">{getPageTitle(location.pathname)}</h1>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/* Credits pill — always visible if can read wallet; clickable only with billing permission */}
          {can('wallet', 'read') && walletTotal !== undefined && (
            canOpenBilling ? (
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-7 gap-1.5 text-xs',
                  walletTotal < 50
                    ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
                onClick={() => navigate('/billing')}
              >
                <IconCoin className="h-3.5 w-3.5" />
                {walletTotal} {t('shell.credits')}
              </Button>
            ) : (
              <div
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs',
                  walletTotal < 50
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-muted text-muted-foreground',
                )}
                aria-label={`${walletTotal} ${t('shell.credits')}`}
              >
                <IconCoin className="h-3.5 w-3.5" />
                {walletTotal} {t('shell.credits')}
              </div>
            )
          )}

          {/* Dark / Light mode toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
          >
            {isDark
              ? <IconSun className="h-4 w-4" />
              : <IconMoon className="h-4 w-4" />
            }
          </Button>
        </div>
      </div>
    </header>
  )
}
